import { c as _c } from "react/compiler-runtime";
import { deepOmit } from "@website/utils/deep-omit";
import { isFilled } from "@website/utils/is-filled";
import { getStripFilterOptionStaggerKey } from "@website/widget/filter/filter-components/strip.utils";
import type { StripTypes } from "@website/widget/filter/filter.types";
import type { ItemStaggerPropertiesGetter } from "@website/widget/item-stagger/item-stagger.hook";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { Children, isValidElement, useEffect, useRef, useState, type ReactNode, type TransitionEvent } from "react";
interface StripFilterProperties extends StripTypes {
  getItemStaggerProperties?: ItemStaggerPropertiesGetter;
  itemStaggerIndexes?: Record<string, number>;
  name: string;
}
type StripEntryDirection = "bottom" | "top";
type StripOptionSelection = {
  checked: boolean;
  option: string;
  routerValue: string;
};
export const getStripFilterContainerClassName = (isStripFilter: boolean, shouldAnimate = true) => `relative flex shrink-0 ${isStripFilter ? `${shouldAnimate ? "animate-strip-gradient-border" : ""} overflow-hidden rounded-full bg-[linear-gradient(100deg,var(--color-error)_0%,#9b47b5_35%,var(--color-secondary)_50%,#9b47b5_65%,var(--color-error)_100%)] bg-[length:200%_100%] p-0.5 shadow-[0_4px_10px_rgba(97,100,117,0.14)]` : ""}`;
export const getStripFilterButtonClassName = (isStripFilter: boolean) => `!whitespace-nowrap transition-[border-color,box-shadow,color,fill] duration-300 ease-in-out ${isStripFilter ? "!h-8 !border-0 !bg-white !fill-on-surface-high-emphasis !text-on-surface-high-emphasis" : ""}`;
type StripFilterPanelProperties = {
  children: ReactNode;
  entryDirection: StripEntryDirection;
  isExiting: boolean;
  onTransitionEnd(event: TransitionEvent<HTMLDivElement>): void;
  renderedStripKey?: string;
};
const STRIP_FALLBACK_COLLAPSE_SCROLL_Y = 120;
const STRIP_FALLBACK_EXPAND_SCROLL_Y = 24;
const getPageScrollY = () => typeof window === "undefined" ? 0 : window.scrollY;
const isPageNearTop = () => getPageScrollY() <= STRIP_FALLBACK_EXPAND_SCROLL_Y;
const isPagePastFallbackCollapse = () => getPageScrollY() > STRIP_FALLBACK_COLLAPSE_SCROLL_Y;
const renderStripPanelContent = (children: ReactNode, filterKey: string) => Children.map(children, child => isValidElement(child) && child.key === filterKey ? child : null);
const getStripContainerClassName = (isExiting: boolean, entryDirection: StripEntryDirection) => {
  if (isExiting) return "-translate-y-1 grid-rows-[0fr] opacity-100";
  return entryDirection === "top" ? "animate-slide-top-fade grid-rows-[1fr] opacity-100" : "animate-slide-bottom-fade grid-rows-[1fr] opacity-100";
};
const getToggledStripValues = (values: Array<string>, option: string) => values.includes(option) ? values.filter(item => item !== option) : [...values, option];
export const useStripFilterAnimation = (defaultStripKey, triggerInView) => {
  const $ = _c(44);
  const initialStripKey = triggerInView ? defaultStripKey : undefined;
  const [activeStripKey, setActiveStripKey] = useState(initialStripKey);
  const [renderedStripKey, setRenderedStripKey] = useState(initialStripKey);
  const [entryDirection, setEntryDirection] = useState("bottom");
  const [manualOverride, setManualOverride] = useState();
  const [isExiting, setIsExiting] = useState(false);
  const isExitingReference = useRef(false);
  const openedFromPageTopReference = useRef(false);
  const triggerInViewReference = useRef(triggerInView);
  const previousTriggerInViewReference = useRef(triggerInView);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = key => {
      setRenderedStripKey(key);
      setActiveStripKey(undefined);
      isExitingReference.current = true;
      setIsExiting(true);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const closeRenderedStrip = t0;
  let t1;
  let t2;
  if ($[1] !== triggerInView) {
    t1 = () => {
      triggerInViewReference.current = triggerInView;
    };
    t2 = [triggerInView];
    $[1] = triggerInView;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== defaultStripKey) {
    t3 = () => {
      setManualOverride(undefined);
      if (!defaultStripKey) {
        setActiveStripKey(undefined);
        setRenderedStripKey(undefined);
        return;
      }
      const shouldOpenFromPageTop = !triggerInViewReference.current && isPageNearTop();
      if (!triggerInViewReference.current && !shouldOpenFromPageTop) {
        setActiveStripKey(undefined);
        setRenderedStripKey(undefined);
        openedFromPageTopReference.current = false;
        isExitingReference.current = false;
        setIsExiting(false);
        return;
      }
      openedFromPageTopReference.current = shouldOpenFromPageTop;
      setEntryDirection("bottom");
      setActiveStripKey(defaultStripKey);
      setRenderedStripKey(defaultStripKey);
      isExitingReference.current = false;
      setIsExiting(false);
    };
    t4 = [defaultStripKey];
    $[4] = defaultStripKey;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== activeStripKey || $[8] !== renderedStripKey || $[9] !== triggerInView) {
    t5 = () => {
      const wasTriggerInView = previousTriggerInViewReference.current;
      if (triggerInView && !previousTriggerInViewReference.current) {
        setManualOverride(undefined);
      }
      if (!triggerInView && wasTriggerInView) {
        const stripKey = activeStripKey || renderedStripKey;
        if (stripKey) {
          setManualOverride(undefined);
          setRenderedStripKey(stripKey);
          setActiveStripKey(undefined);
          openedFromPageTopReference.current = false;
          isExitingReference.current = true;
          setIsExiting(true);
        }
      }
      previousTriggerInViewReference.current = triggerInView;
    };
    t6 = [activeStripKey, renderedStripKey, triggerInView];
    $[7] = activeStripKey;
    $[8] = renderedStripKey;
    $[9] = triggerInView;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[12] !== activeStripKey || $[13] !== defaultStripKey || $[14] !== manualOverride || $[15] !== renderedStripKey || $[16] !== triggerInView) {
    t7 = () => {
      if (!defaultStripKey || !triggerInView || manualOverride || activeStripKey || renderedStripKey || isExitingReference.current) {
        return;
      }
      setEntryDirection("bottom");
      openedFromPageTopReference.current = false;
      setActiveStripKey(defaultStripKey);
    };
    t8 = [activeStripKey, defaultStripKey, manualOverride, renderedStripKey, triggerInView];
    $[12] = activeStripKey;
    $[13] = defaultStripKey;
    $[14] = manualOverride;
    $[15] = renderedStripKey;
    $[16] = triggerInView;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[19] !== activeStripKey || $[20] !== manualOverride) {
    t9 = () => {
      if (manualOverride !== "closed" || !activeStripKey) {
        return;
      }
      setRenderedStripKey(activeStripKey);
      setActiveStripKey(undefined);
      openedFromPageTopReference.current = false;
      isExitingReference.current = true;
      setIsExiting(true);
    };
    t10 = [activeStripKey, manualOverride];
    $[19] = activeStripKey;
    $[20] = manualOverride;
    $[21] = t10;
    $[22] = t9;
  } else {
    t10 = $[21];
    t9 = $[22];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[23] !== activeStripKey || $[24] !== defaultStripKey || $[25] !== manualOverride || $[26] !== renderedStripKey) {
    t11 = () => {
      if (typeof window === "undefined" || !defaultStripKey || manualOverride) {
        return;
      }
      const handleFallbackScroll = () => {
        const stripKey_0 = activeStripKey || renderedStripKey;
        const pageIsNearTop = isPageNearTop();
        if (stripKey_0 && !isExitingReference.current && isPagePastFallbackCollapse()) {
          openedFromPageTopReference.current = false;
          setManualOverride(undefined);
          setRenderedStripKey(stripKey_0);
          setActiveStripKey(undefined);
          isExitingReference.current = true;
          setIsExiting(true);
          return;
        }
        if (pageIsNearTop) {
          openedFromPageTopReference.current = true;
          isExitingReference.current = false;
          setEntryDirection("bottom");
          setActiveStripKey(defaultStripKey);
          setRenderedStripKey(defaultStripKey);
          setIsExiting(false);
        }
      };
      window.addEventListener("scroll", handleFallbackScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleFallbackScroll);
    };
    t12 = [activeStripKey, defaultStripKey, manualOverride, renderedStripKey];
    $[23] = activeStripKey;
    $[24] = defaultStripKey;
    $[25] = manualOverride;
    $[26] = renderedStripKey;
    $[27] = t11;
    $[28] = t12;
  } else {
    t11 = $[27];
    t12 = $[28];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[29] !== activeStripKey) {
    t13 = () => {
      if (!activeStripKey) {
        if (isExitingReference.current) {
          return;
        }
        isExitingReference.current = false;
        setRenderedStripKey(undefined);
        setIsExiting(false);
        return;
      }
      setRenderedStripKey(activeStripKey);
      if (!isExitingReference.current) {
        return;
      }
      isExitingReference.current = false;
      setEntryDirection("top");
      setIsExiting(false);
    };
    t14 = [activeStripKey];
    $[29] = activeStripKey;
    $[30] = t13;
    $[31] = t14;
  } else {
    t13 = $[30];
    t14 = $[31];
  }
  useEffect(t13, t14);
  let t15;
  if ($[32] !== activeStripKey || $[33] !== renderedStripKey) {
    t15 = () => {
      const stripKey_1 = activeStripKey || renderedStripKey;
      if (!stripKey_1) {
        return;
      }
      openedFromPageTopReference.current = false;
      setManualOverride("closed");
      closeRenderedStrip(stripKey_1);
    };
    $[32] = activeStripKey;
    $[33] = renderedStripKey;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const closeVisibleStrip = t15;
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = key_0 => {
      openedFromPageTopReference.current = false;
      setManualOverride("open");
      setActiveStripKey(key_0);
      setRenderedStripKey(key_0);
      setEntryDirection("top");
      isExitingReference.current = false;
      setIsExiting(false);
    };
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const openStrip = t16;
  let t17;
  if ($[36] !== activeStripKey) {
    t17 = event => {
      if (event.target !== event.currentTarget) {
        return;
      }
      if (activeStripKey || !isExitingReference.current) {
        return;
      }
      isExitingReference.current = false;
      setIsExiting(false);
      setRenderedStripKey(undefined);
    };
    $[36] = activeStripKey;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const handleTransitionEnd = t17;
  let t18;
  if ($[38] !== closeVisibleStrip || $[39] !== entryDirection || $[40] !== handleTransitionEnd || $[41] !== isExiting || $[42] !== renderedStripKey) {
    t18 = {
      closeVisibleStrip,
      entryDirection,
      handleTransitionEnd,
      isExiting,
      openStrip,
      renderedStripKey
    };
    $[38] = closeVisibleStrip;
    $[39] = entryDirection;
    $[40] = handleTransitionEnd;
    $[41] = isExiting;
    $[42] = renderedStripKey;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
};
export const StripFilterPanel = properties => {
  const $ = _c(14);
  const {
    children,
    entryDirection,
    isExiting,
    onTransitionEnd,
    renderedStripKey
  } = properties;
  if (!renderedStripKey) {
    return null;
  }
  let t0;
  if ($[0] !== entryDirection || $[1] !== isExiting) {
    t0 = getStripContainerClassName(isExiting, entryDirection);
    $[0] = entryDirection;
    $[1] = isExiting;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = `grid w-full transform-gpu overflow-hidden transition-[grid-template-rows,opacity,transform] [contain:layout_paint] ${isExiting ? "duration-200 ease-out" : "duration-300 ease-in-out"} will-change-[opacity,transform] ${t0}`;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden={true} style={{
      height: 14
    }} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== renderedStripKey) {
    t3 = renderStripPanelContent(children, renderedStripKey);
    $[4] = children;
    $[5] = renderedStripKey;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="min-h-0 overflow-hidden">{t2}{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onTransitionEnd || $[10] !== renderedStripKey || $[11] !== t1 || $[12] !== t4) {
    t5 = <div className={t1} key={renderedStripKey} onTransitionEnd={onTransitionEnd}>{t4}</div>;
    $[9] = onTransitionEnd;
    $[10] = renderedStripKey;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export const StripFilter = properties => {
  const $ = _c(2);
  const {
    getItemStaggerProperties,
    itemStaggerIndexes,
    name,
    options: t0,
    onChange
  } = properties;
  const options = t0 === undefined ? [] : t0;
  const router = useRouter();
  const routerValue = toString(router.query?.[name]);
  const [pendingValue, setPendingValue] = useState();
  const previewedSelectionReference = useRef(undefined);
  const valueArray = (pendingValue ?? routerValue).split(",").filter(Boolean);
  const isChecked = option => valueArray.includes(option);
  const getOptionSelection = option_0 => {
    const nextValue = getToggledStripValues(valueArray, option_0);
    return {
      checked: nextValue.includes(option_0),
      option: option_0,
      routerValue: nextValue.join(",")
    };
  };
  const previewOption = option_1 => {
    const selection = getOptionSelection(option_1);
    previewedSelectionReference.current = selection;
    setPendingValue(selection.routerValue);
  };
  const cancelOptionPreview = () => {
    if (!previewedSelectionReference.current) {
      return;
    }
    previewedSelectionReference.current = undefined;
    setPendingValue(undefined);
  };
  const toggleOption = (label, option_2) => {
    const previewedSelection = previewedSelectionReference.current;
    const selection_0 = previewedSelection?.option === option_2 ? previewedSelection : getOptionSelection(option_2);
    previewedSelectionReference.current = undefined;
    setPendingValue(selection_0.routerValue);
    const clearPendingValue = () => setPendingValue(currentValue => currentValue === selection_0.routerValue ? undefined : currentValue);
    router.replace({
      pathname: router?.pathname,
      query: deepOmit({
        ...router.query,
        [name]: selection_0.routerValue
      }, isFilled)
    }, undefined, {
      shallow: true,
      scroll: false
    }).then(clearPendingValue, clearPendingValue);
    onChange?.({
      name: option_2,
      label,
      isChecked: selection_0.checked
    });
  };
  const t1 = "scrollbar-none flex w-full min-w-0 flex-row items-start justify-start gap-6 overflow-x-auto overflow-y-hidden px-2 pt-1";
  const t2 = options.map((option_3, index) => {
    const checked = isChecked(option_3.name);
    const itemStaggerKey = getStripFilterOptionStaggerKey(name, option_3.name);
    const itemStaggerProperties = getItemStaggerProperties?.(itemStaggerKey, itemStaggerIndexes?.[itemStaggerKey] ?? index, {
      className: "max-md:!translate-y-0 max-md:!opacity-100 max-md:!transition-none shrink-0"
    }) ?? {
      className: "max-md:!translate-y-0 max-md:!opacity-100 max-md:!transition-none shrink-0"
    };
    const iconClassName = `text-headline-5 ${checked ? "text-on-surface-high-emphasis opacity-100" : "text-on-surface-medium-emphasis opacity-60"}`;
    return <div key={option_3.name} {...itemStaggerProperties}><button aria-pressed={checked} className={`flex min-w-[64px] shrink-0 cursor-pointer select-none flex-col items-center justify-start gap-1 border-0 bg-transparent p-0 ${checked ? "text-on-surface-high-emphasis" : "text-on-surface-medium-emphasis"}`} onClick={() => toggleOption(option_3.label, option_3.name)} onPointerCancel={cancelOptionPreview} onPointerDown={() => previewOption(option_3.name)} onPointerLeave={event => {
        if (event.pointerType === "mouse") {
          cancelOptionPreview();
        }
      }} style={{
        touchAction: "manipulation"
      }} type="button">{option_3.icon && <span className="flex h-7 items-center justify-center"><span className={iconClassName}>{option_3.icon}</span></span>}<span className={`text-caption whitespace-nowrap ${checked ? "font-bold" : "font-normal"}`}>{option_3.label}</span></button></div>;
  });
  let t3;
  if ($[0] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  return t3;
};